import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Table, Button, ButtonGroup, Badge, Alert, Spinner, Modal, Form, Nav, Tabs, Tab } from 'react-bootstrap';
import { DataTable } from '../components/common/DataTable';
import { FilterBar, type FilterDefinition } from '../components/common/FilterControls';
import DatePeriodNavigation, { PeriodChangeEvent } from '../components/common/DatePeriodNavigation';
import type { ColumnConfig } from '../components/common/DataTable/types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import TimesheetPeriodNavigation from '../components/timesheets/TimesheetPeriodNavigation';
import TimesheetWeeklyGrid, { EntryData, ViewMode } from '../components/timesheets/TimesheetWeeklyGrid';
import TimesheetSummary from '../components/timesheets/TimesheetSummary';
import { useAuth } from '../hooks/useAuth';
import {
  useGetUsersId,
  useGetTimesheetsUsersUserIdCurrentPeriod,
  useGetTimesheets,
  getTimesheets,
  getUsers,
  usePostTimesheetsUsersUserId,
  useGetPayrollActivities,
  useGetPayrollUsersUserIdConfig,
  useDeleteTimesheetsTimesheetId,
  useGetTimesheetsTimesheetId,
  usePostInvoicePaymentsPaymentIdRefresh,
  useGetPayrollUsersUserIdConfig as useGetReviewUserConfig,
  getGetTimesheetsQueryKey,
  getGetTimesheetsTimesheetIdQueryKey,
  getGetPayrollActivitiesQueryKey,
  getGetTimesheetsUsersUserIdCurrentPeriodQueryKey,
  getGetPayrollUsersUserIdConfigQueryKey,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import { Timesheet, TimesheetStatus, TimesheetEntry, PayrollActivity, TimesheetsSummary, User } from '../api/generated/model';
import { usePublicSettings } from '../hooks/usePublicSettings';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import {
  useTimesheetActions,
  rejectionOps,
  paymentReferenceOps,
  sendPaymentOps,
} from '../hooks/useTimesheetActions';
import { formatHours, parseHoursInput } from '../utils/hours';

// One page of the team list. Matches the cursor's own default.
const TEAM_FETCH_LIMIT = 50;

// Filter keys the team list understands. Each is mirrored into the URL with a
// team_ prefix, so the "me" tab's own params can't collide with them.
const TEAM_FILTER_KEYS = ['status', 'payment_status', 'user_id'] as const;

const userLabel = (u: User): string =>
  `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || u.id || '';

// Employee picker, server-side. The user table is unbounded, so the dropdown
// searches and pages rather than loading everyone up front — the same shape
// the customer picker uses on the financial reports filter bar.
const userLoadPage = async ({ search, cursor, limit }: { search: string; cursor?: string; limit: number }) => {
  const result: any = await getUsers({
    ...(cursor ? { cursor } : {
      ...(search ? { search } : {}),
      sort_field: 'last_name' as any,
      sort_direction: 'asc' as any,
    }),
    limit,
  });
  return {
    items: (result?.data ?? []) as User[],
    // The backend's Pagination schema names this `cursor`, not next_cursor.
    nextCursor: result?.pagination?.cursor as string | undefined,
  };
};

// Labels an already-selected employee who isn't on the page currently loaded.
// Without it a saved filter renders as a bare uuid.
const userLoadByIds = async (ids: string[]) => {
  if (ids.length === 0) return [];
  const result: any = await getUsers({
    ids: ids.join(','),
    // sort_field and sort_direction are required by the cursor library on any
    // request without a cursor, even one fetching a fixed set of ids. Omitting
    // them is a 400, not a default.
    sort_field: 'last_name' as any,
    sort_direction: 'asc' as any,
    limit: ids.length,
  } as any);
  return (result?.data ?? []) as User[];
};

const formatPeriod = (start?: string, end?: string): string => {
  if (!start || !end) return '';
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  const sDow = s.toLocaleDateString('en-US', { weekday: 'short' });
  const eDow = e.toLocaleDateString('en-US', { weekday: 'short' });
  const sMonth = s.toLocaleDateString('en-US', { month: 'short' });
  const eMonth = e.toLocaleDateString('en-US', { month: 'short' });
  return `${sDow} ${sMonth} ${s.getDate()} - ${eDow} ${eMonth} ${e.getDate()}, ${e.getFullYear()}`;
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
};

const statusBadge = (status: string) => {
  const variants: Record<string, string> = {
    draft: 'secondary',
    submitted: 'primary',
    approved: 'success',
    rejected: 'danger',
    processing: 'warning',
    paid: 'info',
  };
  return (
    <Badge bg={variants[status] || 'secondary'} className="text-capitalize">
      {status}
    </Badge>
  );
};

// Round a USD amount to the nearest cent.
const roundCents = (amount: number): number => Math.round(amount * 100) / 100;

// Find the effective rate for an activity on a given date.
// Priority: (1) user default hourly rate, (2) activity rate schedule.
const findEffectiveRate = (defaultRate: number | undefined, activity: PayrollActivity | undefined, entryDate: string): number => {
  if (defaultRate != null) return defaultRate;
  if (!activity?.rates?.length) return 0;
  for (let i = activity.rates.length - 1; i >= 0; i--) {
    const r = activity.rates[i];
    if (r.start_date && r.start_date <= entryDate) {
      return r.hourly_rate ?? 0;
    }
  }
  return 0;
};

const validTabs = ['me', 'team'];

// ========== My Timesheets tab content ==========
const MyTimesheetsContent: React.FC<{
  historyLoading: boolean;
  allTimesheets: any[];
  openEditor: (date: string) => void;
  userId?: string;
  userPreferences?: any;
}> = ({ historyLoading, allTimesheets, openEditor, userId, userPreferences }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortField, setSortField] = useState(searchParams.get('sort_field') || 'period');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>((searchParams.get('sort_dir') as 'asc' | 'desc') || 'desc');

  const handleSortChange = (field: string, dir: 'asc' | 'desc') => {
    setSortField(field);
    setSortDir(dir);
    const params = new URLSearchParams(searchParams);
    params.set('sort_field', field);
    params.set('sort_dir', dir);
    setSearchParams(params, { replace: true });
  };
  return (
  <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: 'auto' }}>
    <div className="d-flex justify-content-end mb-3">
      <Button variant="primary" onClick={() => openEditor(new Date().toLocaleDateString('en-CA'))}>
        <i className="bi bi-plus-lg me-1"></i>New Timesheet
      </Button>
    </div>

    {historyLoading && (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading timesheets...
      </div>
    )}

    {!historyLoading && allTimesheets.length === 0 && (
      <Card>
        <Card.Body className="text-center py-4 text-muted">
          <i className="bi bi-clock-history fs-1 d-block mb-2"></i>
          No timesheets yet. Click "New Timesheet" to get started.
        </Card.Body>
      </Card>
    )}

    {!historyLoading && allTimesheets.length > 0 && (
      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
        <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
          <DataTable
            data={allTimesheets}
            columns={[
              {
                key: 'period', label: 'Period', width: 200, sortable: true,
                sortValue: (ts: any) => ts.period_start || '',
                renderCell: (ts: any) => formatPeriod(ts.period_start, ts.period_end),
              },
              {
                key: 'status', label: 'Status', width: 120, sortable: true,
                renderCell: (ts: any) => statusBadge(ts.status || 'draft'),
              },
              {
                key: 'total_hours', label: 'Hours', width: 100, sortable: true,
                renderCell: (ts: any) => <span className="d-block text-end">{formatHours(ts.total_hours ?? 0)}</span>,
              },
              {
                key: 'total_amount', label: 'Amount', width: 120, sortable: true,
                renderCell: (ts: any) => <span className="d-block text-end">${(ts.total_amount ?? 0).toFixed(2)}</span>,
              },
            ] as ColumnConfig<any>[]}
            getRowKey={(ts: any) => ts.id || ''}
            onRowClick={(ts: any) => { if (ts.period_start) openEditor(ts.period_start); }}
            sortConfig={{ field: sortField, direction: sortDir }}
            onSortChange={handleSortChange}
            enableColumnReorder={true}
            enableColumnResize={true}
            enableColumnVisibility={true}
            preferencesKey="my_timesheets"
            userId={userId}
            userPreferences={userPreferences}
            enableStickyHeader={true}
            maxHeight="100%"
          />
        </Card.Body>
      </Card>
    )}
  </div>
  );
};

// ========== Team Timesheets tab content ==========
const TeamTimesheetsContent: React.FC<{
  reviewLoading: boolean;
  pendingTimesheets: any[];
  approvedTimesheets: any[];
  isAdmin: boolean;
  selectedTimesheetIds: Set<string>;
  toggleAllApproved: () => void;
  toggleTimesheetSelection: (id: string) => void;
  openReviewModal: (id: string) => void;
  // Each filter value is a tri-state CSV: undefined = no filter (show all),
  // '' = explicit deselect-all (match nothing), 'a,b' = subset. Don't
  // collapse undefined and '' — see FinancialReportsFilterBar:178.
  filters: Record<string, any>;
  onFilterChange: (field: string, value: string | undefined) => void;
  onClearFilters: () => void;
  period: string;
  dateRange: { start_date: string; end_date: string };
  onPeriodChange: (e: PeriodChangeEvent) => void;
  summary?: TimesheetsSummary;
  setSelectedTimesheetIds: (v: Set<string>) => void;
  paymentReference: string;
  setPaymentReference: (v: string) => void;
  handleBatchPay: () => void;
  batchPaying: boolean;
  batchPayError: string | null;
  setBatchPayError: (v: string | null) => void;
  userId?: string;
  userPreferences?: any;
  // Sorting and paging are server-side now, so the table no longer owns
  // either — it reports what the user clicked and renders what comes back.
  sortField: string;
  sortDir: 'asc' | 'desc';
  handleSortChange: (field: string, dir: 'asc' | 'desc') => void;
  hasMore: boolean;
  onLoadMore: () => void;
  totalCount?: number;
}> = ({
  reviewLoading, pendingTimesheets, approvedTimesheets, isAdmin,
  selectedTimesheetIds, toggleAllApproved, toggleTimesheetSelection, openReviewModal,
  filters, onFilterChange, onClearFilters, period, dateRange, onPeriodChange, summary,
  setSelectedTimesheetIds,
  paymentReference, setPaymentReference, handleBatchPay, batchPaying, batchPayError, setBatchPayError,
  userId, userPreferences,
  sortField, sortDir, handleSortChange, hasMore, onLoadMore, totalCount,
}) => {
  const [showFilterBar, setShowFilterBar] = useState<boolean>(
    () => TEAM_FILTER_KEYS.some(k => filters[k] !== undefined)
  );

  // Read selection through a ref inside renderCell. DataTable seeds its
  // column state with useState(defaultColumns), so the column objects — and
  // every closure inside them — are captured on first render and never
  // refreshed. A cell reading selectedTimesheetIds directly kept seeing the
  // empty Set from mount, which is why a ticked box never showed a tick. A
  // ref's .current is live even inside a stale closure.
  const selectedIdsRef = useRef(selectedTimesheetIds);
  selectedIdsRef.current = selectedTimesheetIds;


  const activeFilterCount = useMemo(
    // Counts a deselect-all ('') as active: it is very much a filter, and the
    // one most worth flagging, since it is why the table looks empty.
    () => TEAM_FILTER_KEYS.filter(k => filters[k] !== undefined).length,
    [filters]
  );

  const filterDefs = useMemo(() => [
    {
      type: 'multiselect' as const,
      field: 'status',
      label: 'Status',
      icon: 'bi-check-circle',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'submitted', label: 'Submitted' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'processing', label: 'Processing' },
        { value: 'paid', label: 'Paid' },
      ],
    },
    {
      type: 'multiselect' as const,
      field: 'payment_status',
      label: 'Payment',
      icon: 'bi-cash-stack',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'processing', label: 'Processing' },
        { value: 'sent', label: 'Sent' },
        { value: 'failed', label: 'Failed' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'cancelled', label: 'Cancelled' },
      ],
    },
    {
      type: 'multiselect' as const,
      field: 'user_id',
      label: 'Employee',
      icon: 'bi-person',
      placeholder: 'Search employees…',
      // Async, not a static option list. The set of employees is the whole
      // user table, which is unbounded and not something to ship down on
      // every query; it also must not be derived from who already has a
      // timesheet, or anyone yet to file one — or whose timesheets were
      // disabled since — silently becomes unselectable.
      loadPage: userLoadPage,
      loadByIds: userLoadByIds,
      getOptionValue: (u: User) => u.id || '',
      getOptionLabel: (u: User) => userLabel(u),
    },
  ] as FilterDefinition[], []);

  return (
  <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
    <style>{`
      .team-timesheets-summary-cards-row {
        display: flex !important;
        flex-wrap: wrap !important;
        gap: 1rem !important;
        margin-bottom: 1rem !important;
      }
      .team-timesheets-summary-card-col {
        min-width: 200px !important;
        flex: 1 1 200px !important;
      }
    `}</style>

    {/* Totals across everything matching the filters, not just the rows
        scrolled into view — they come off the cursor's summary, so they do
        not change as more pages load. */}
    <div className="team-timesheets-summary-cards-row">
      <div className="team-timesheets-summary-card-col">
        <Card className="h-100">
          <Card.Body className="text-center p-2">
            <div className="mb-2"><i className="bi bi-clock-history fs-1 text-primary"></i></div>
            <h3 className="mb-1">{summary?.total_count ?? 0}</h3>
            <p className="text-muted mb-0 small">Timesheets</p>
          </Card.Body>
        </Card>
      </div>
      <div className="team-timesheets-summary-card-col">
        <Card className="h-100">
          <Card.Body className="text-center p-2">
            <div className="mb-2"><i className="bi bi-hourglass-split fs-1 text-info"></i></div>
            <h3 className="mb-1">{formatHours(summary?.total_hours ?? 0)}</h3>
            <p className="text-muted mb-0 small">Total Hours</p>
          </Card.Body>
        </Card>
      </div>
      <div className="team-timesheets-summary-card-col">
        <Card className="h-100">
          <Card.Body className="text-center p-2">
            <div className="mb-2"><i className="bi bi-cash-coin fs-1 text-success"></i></div>
            <h3 className="mb-1">${(summary?.total_amount ?? 0).toFixed(2)}</h3>
            <p className="text-muted mb-0 small">Total Amount</p>
          </Card.Body>
        </Card>
      </div>
    </div>

    {batchPayError && (
      <Alert variant="danger" dismissible onClose={() => setBatchPayError(null)} className="mb-3">
        {batchPayError}
      </Alert>
    )}

    <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
      <Card.Header className="bg-primary text-white flex-shrink-0">
        <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
              <span className="text-primary fw-semibold">
                <i className="bi bi-clock me-1"></i>
                Timesheets for
              </span>
              <DatePeriodNavigation
                onPeriodChange={onPeriodChange}
                dateFormat="date-only"
                initialNavigationMode={period as any}
                initialDateRange={{ start_date: dateRange.start_date, end_date: dateRange.end_date }}
              />
              <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>
              <Button
                variant={activeFilterCount > 0 ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setShowFilterBar(!showFilterBar)}
              >
                <i className="bi bi-funnel me-1"></i>
                Filters
                {activeFilterCount > 0 && (
                  <span className="badge bg-header-control text-primary ms-1">{activeFilterCount}</span>
                )}
              </Button>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {isAdmin && selectedTimesheetIds.size > 0 && (
              <>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Payment reference"
                  value={paymentReference}
                  onChange={e => setPaymentReference(e.target.value)}
                  style={{ width: '220px' }}
                  className="bg-header-control text-primary border-primary"
                />
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="bg-header-control"
                  onClick={handleBatchPay}
                  disabled={batchPaying}
                >
                  {batchPaying ? (
                    <><Spinner animation="border" size="sm" className="me-1" />Paying...</>
                  ) : (
                    <><i className="bi bi-cash-stack me-1"></i>Mark {selectedTimesheetIds.size} as Paid</>
                  )}
                </Button>
              </>
            )}
            <div className="bg-header-control rounded px-3 py-1">
              <span className="text-primary">
                {totalCount ?? pendingTimesheets.length}{' '}
                {(totalCount ?? pendingTimesheets.length) === 1 ? 'timesheet' : 'timesheets'}
              </span>
            </div>
          </div>
        </div>
        {showFilterBar && (
          <FilterBar
            filters={filters}
            filterDefs={filterDefs}
            onFilterChange={onFilterChange}
            onClearFilters={onClearFilters}
          />
        )}
      </Card.Header>
      <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
        {reviewLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading timesheets...
          </div>
        ) : (
          <DataTable
            data={pendingTimesheets}
            columns={[
              ...(isAdmin ? [{
                key: 'select', label: 'Select', width: 60,
                // The cell owns the click, not the input. The input is purely
                // visual — readOnly, untabbable, pointer-events off — so the
                // whole cell is the hit target and the row's own onRowClick
                // doesn't also fire and open the review modal.
                onCellClick: (ts: any) => {
                  if (ts.status === 'approved' && ts.id) toggleTimesheetSelection(ts.id);
                },
                renderCell: (ts: any) => ts.status === 'approved' ? (
                  <div className="d-flex justify-content-center" style={{ cursor: 'pointer', pointerEvents: 'none' }}>
                    <Form.Check
                      type="checkbox"
                      checked={selectedIdsRef.current.has(ts.id!)}
                      readOnly
                      tabIndex={-1}
                      aria-label="Select timesheet for payment"
                      style={{ margin: 0 }}
                    />
                  </div>
                ) : null,
              }] : []),
              {
                key: 'employee', label: 'Employee', width: 200, sortable: true,
                renderCell: (ts: any) => (
                  <div>
                    {ts.user?.first_name} {ts.user?.last_name}
                    <br /><small className="text-muted">{ts.user?.email}</small>
                  </div>
                ),
              },
              {
                key: 'period', label: 'Period', width: 180, sortable: true,
                renderCell: (ts: any) => formatPeriod(ts.period_start, ts.period_end),
              },
              {
                key: 'status', label: 'Status', width: 120, sortable: true,
                renderCell: (ts: any) => statusBadge(ts.status || 'submitted'),
              },
              {
                key: 'payment', label: 'Payment', width: 120, sortable: true,
                renderCell: (ts: any) => ts.payment ? (
                  <Badge bg={
                    ts.payment.status === 'sent' ? 'success' :
                    ts.payment.status === 'failed' ? 'danger' :
                    ts.payment.status === 'rejected' ? 'danger' :
                    ts.payment.status === 'cancelled' ? 'secondary' :
                    'warning'
                  }>
                    {ts.payment.status_label || ts.payment.status}
                  </Badge>
                ) : <span className="text-muted">-</span>,
              },
              {
                key: 'total_hours', label: 'Total Hours', width: 110, sortable: true,
                renderCell: (ts: any) => <span className="d-block text-end">{formatHours(ts.total_hours ?? 0)}</span>,
              },
              {
                key: 'total_amount', label: 'Total Amount', width: 130, sortable: true,
                renderCell: (ts: any) => <span className="d-block text-end">${(ts.total_amount ?? 0).toFixed(2)}</span>,
              },
              {
                key: 'submitted_at', label: 'Submitted', width: 150, sortable: true,
                renderCell: (ts: any) => formatDate(ts.submitted_at),
              },
            ] as ColumnConfig<any>[]}
            getRowKey={(ts: any) => ts.id || ''}
            onRowClick={(ts: any) => openReviewModal(ts.id!)}
            sortConfig={{ field: sortField, direction: sortDir }}
            onSortChange={handleSortChange}
            // Passing pagination puts DataTable in server-sort mode: the
            // header reports the click and the cursor re-queries, instead of
            // the table reordering whatever subset happens to be loaded.
            pagination={{ hasMore, limit: TEAM_FETCH_LIMIT }}
            onLoadMore={onLoadMore}
            isLoading={reviewLoading}
            enableColumnReorder={true}
            enableColumnResize={true}
            enableColumnVisibility={true}
            preferencesKey="team_timesheets"
            userId={userId}
            userPreferences={userPreferences}
            enableStickyHeader={true}
            maxHeight="100%"
            emptyState={
              <div className="text-center text-muted py-4">
                <i className="bi bi-check-circle fs-1 d-block mb-2"></i>
                No team timesheets found.
              </div>
            }
          />
        )}
        </Card.Body>
      </Card>
  </div>
  );
};

const TimesheetsPage: React.FC = () => {
  const { user, timesheetReports } = useAuth();
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tab } = useParams<{ tab: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-based tab
  const activeTab = tab && validTabs.includes(tab) ? tab : 'me';

  // Editor modal state
  const [showEditor, setShowEditor] = useState(false);
  const [editorDate, setEditorDate] = useState<string>(new Date().toLocaleDateString('en-CA'));
  const [localEntries, setLocalEntries] = useState<EntryData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [localNotes, setLocalNotes] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  // Tracks a pending action blocked by unsaved changes: 'close' or a date string (navigate)
  const [unsavedAction, setUnsavedAction] = useState<string | null>(null);

  // Review modal state
  const [reviewTimesheetId, setReviewTimesheetId] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewProcessingAction, setReviewProcessingAction] = useState<string | null>(null);
  const [reviewRejectReason, setReviewRejectReason] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const paymentRefSection = useRef<HTMLDivElement>(null);
  const [confirmUnpay, setConfirmUnpay] = useState(false);
  const [confirmUnapprove, setConfirmUnapprove] = useState(false);
  const [showPayConfirm, setShowPayConfirm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showMarkAsPaidForm, setShowMarkAsPaidForm] = useState(false);
  const [payMemo, setPayMemo] = useState('');

  // Review tab filters and batch selection
  // Team list state, all of it reflected in the URL so a reload or a shared
  // link restores the same view. The status filter used not to be, so it was
  // silently lost on refresh while the sort survived.
  //
  // searchParams.has() rather than a truthiness check on the value: '' is the
  // deselect-all sentinel and means "match nothing", which is the opposite of
  // "no filter". Dropping it would widen the view instead of emptying it.
  // Read with has() rather than a truthiness check on the value: '' is the
  // deselect-all sentinel and means "match nothing", the opposite of "no
  // filter". Dropping it on reload would widen the view instead of emptying
  // it, which is the bug the web forms page had.
  const [teamFilters, setTeamFilters] = useState<Record<string, any>>(() => {
    const f: Record<string, any> = {};
    for (const key of TEAM_FILTER_KEYS) {
      if (searchParams.has(`team_${key}`)) {
        f[key] = searchParams.get(`team_${key}`) ?? '';
      }
    }
    return f;
  });
  const [teamPeriod, setTeamPeriod] = useState<string>(() => searchParams.get('team_period') || 'year');
  const [teamDateRange, setTeamDateRange] = useState<{ start_date: string; end_date: string }>(() => {
    const start = searchParams.get('team_start_date');
    const end = searchParams.get('team_end_date');
    if (start || end) return { start_date: start || '', end_date: end || '' };
    // Seed the default window here rather than waiting for
    // DatePeriodNavigation to report one: it does not fire on mount, so the
    // first query would go out unfiltered and the list would visibly narrow a
    // moment later.
    //
    // Built from the year number rather than Date.toISOString(), which
    // renders in UTC — west of Greenwich, January 1 local becomes December 31
    // of the previous year.
    const year = new Date().getFullYear();
    return { start_date: `${year}-01-01`, end_date: `${year}-12-31` };
  });
  const [teamSortField, setTeamSortField] = useState(searchParams.get('team_sort_field') || 'period');
  const [teamSortDir, setTeamSortDir] = useState<'asc' | 'desc'>(
    (searchParams.get('team_sort_dir') as 'asc' | 'desc') || 'desc'
  );

  // The URL is mirrored from an effect, and every handler below is stable —
  // useCallback with no dependencies.
  //
  // Both matter for the same reason. Writing the URL inside the handlers made
  // each one close over the current searchParams, so setSearchParams produced
  // a new searchParams, a new handler, and a new onChange prop on every
  // keystroke — the open filter dropdown was torn down mid-interaction and
  // closed after each click. This is the shape FormSubmissionsTable and
  // InvoicesPage already use, and why their filter bars stay open.
  //
  // searchParams is read through a ref so it stays out of the dep array:
  // depending on it would re-run this effect on its own write and loop. It is
  // merged rather than rebuilt so the "me" tab's own sort params survive.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  useEffect(() => {
    const params = new URLSearchParams(searchParamsRef.current);
    for (const key of TEAM_FILTER_KEYS) {
      const v = teamFilters[key];
      if (v === undefined) params.delete(`team_${key}`);
      else params.set(`team_${key}`, v);
    }
    params.set('team_period', teamPeriod);
    if (teamDateRange.start_date) params.set('team_start_date', teamDateRange.start_date);
    else params.delete('team_start_date');
    if (teamDateRange.end_date) params.set('team_end_date', teamDateRange.end_date);
    else params.delete('team_end_date');
    params.set('team_sort_field', teamSortField);
    params.set('team_sort_dir', teamSortDir);
    setSearchParams(params, { replace: true });
  }, [teamFilters, teamPeriod, teamDateRange, teamSortField, teamSortDir, setSearchParams]);

  const handleTeamFilterChange = useCallback((field: string, value: string | undefined) => {
    setTeamFilters(prev => {
      const next = { ...prev, [field]: value };
      if (value === undefined) delete next[field];
      return next;
    });
    // A row selected under the old filter may not be in the new result set,
    // and batch-paying something no longer on screen is not an action anyone
    // meant to take.
    setSelectedTimesheetIds(new Set());
  }, []);

  const handleTeamClearFilters = useCallback(() => {
    setTeamFilters({});
    setSelectedTimesheetIds(new Set());
  }, []);

  const handleTeamPeriodChange = useCallback((e: PeriodChangeEvent) => {
    // Selecting 'all' emits empty strings, which buildTeamQueryParams drops,
    // so that mode really does mean every timesheet.
    setTeamPeriod(e.navigationMode);
    setTeamDateRange({
      start_date: e.dateRange.start_date.split('T')[0],
      end_date: e.dateRange.end_date.split('T')[0],
    });
    setSelectedTimesheetIds(new Set());
  }, []);

  const handleTeamSortChange = useCallback((field: string, dir: 'asc' | 'desc') => {
    setTeamSortField(field);
    setTeamSortDir(dir);
  }, []);
  const [selectedTimesheetIds, setSelectedTimesheetIds] = useState<Set<string>>(new Set());
  const [batchPaying, setBatchPaying] = useState(false);
  const [batchPayError, setBatchPayError] = useState<string | null>(null);

  // Load user config
  const { data: configData, isLoading: configLoading, isError: configError } = useGetPayrollUsersUserIdConfig(user?.id || '', {
    query: { enabled: !!user?.id },
  });
  const timesheetsEnabled = configData?.timesheets_enabled ?? false;
  const payPeriodType = configData?.pay_period_type || 'biweekly';
  const userDefaultRate = configData?.default_hourly_rate ?? undefined;

  // History table for the signed-in user. This used to be /timesheets/summaries,
  // a second endpoint that existed only because the main list preloaded every
  // entry of every row. The list no longer does, so one endpoint serves both.
  const { data: allTimesheetsData, isLoading: historyLoading } = useGetTimesheets(
    { user_id: user?.id, limit: 100, sort_field: 'period', sort_direction: 'desc' },
    { query: { enabled: timesheetsEnabled && !!user?.id } }
  );
  const allTimesheets: Timesheet[] = allTimesheetsData?.data || [];

  // Load period info for the editor
  const { data: periodData, isLoading: periodLoading } = useGetTimesheetsUsersUserIdCurrentPeriod(
    'me',
    { date: editorDate },
    { query: { enabled: timesheetsEnabled && showEditor } }
  );
  const periodStart = periodData?.period_start || '';
  const periodEnd = periodData?.period_end || '';

  // The timesheet the editor is on. period_start/period_end describe a window
  // the timesheet's own period must overlap, so both ends are passed — with
  // only one, every later period would match. user_id is passed explicitly
  // because an admin's view is otherwise everybody's.
  const { data: timesheetsData, isLoading: timesheetsLoading } = useGetTimesheets(
    {
      period_start: periodStart,
      period_end: periodEnd,
      user_id: user?.id,
      limit: 1,
      // sort_field and sort_direction are required by the cursor library on
      // any request without a cursor — even a one-row lookup that does not
      // care about order. Omitting them is a 400, not a default, and the 400
      // surfaced as "No timesheet exists for this period" over a draft that
      // very much existed.
      sort_field: 'period',
      sort_direction: 'desc',
    },
    { query: { enabled: !!periodStart && !!periodEnd && !!user?.id && showEditor } }
  );

  // The lookup above answers "which timesheet is this period", and nothing
  // more: list rows deliberately carry no entries (timesheets_cursor.go:242),
  // because preloading every entry of every row is what the cursor replaced.
  //
  // So the editor loads the timesheet again by id. Using the list row directly
  // gave the grid an undefined entries array, and a draft with 13.4 hours
  // opened with every day showing zero — the totals in the header still read
  // correctly because they come from the row's own columns, which made it look
  // like the hours had been lost rather than merely not fetched.
  const editorTimesheetId = timesheetsData?.data?.[0]?.id;
  const { data: editorTimesheet } = useGetTimesheetsTimesheetId(
    editorTimesheetId || '',
    { query: { enabled: !!editorTimesheetId && showEditor, staleTime: 0 } }
  );
  const timesheet: Timesheet | undefined = editorTimesheet ?? timesheetsData?.data?.[0];

  // True while the row is known but its entries are still in flight. The grid
  // seeds itself from entries on mount, so it must not mount before they land.
  const entriesPending = !!editorTimesheetId && !editorTimesheet;

  // Load activities
  const { data: activitiesData } = useGetPayrollActivities(
    { period: periodStart },
    { query: { enabled: !!periodStart } }
  );
  const activities = useMemo(() => activitiesData?.data || [], [activitiesData]);

  // Review data
  // reviewStatusFilter is a tri-state from MultiSelectFilter:
  // Each multi-select filter carries the tri-state the shared FilterBar emits
  // and the cursor understands unchanged: absent omits the param (no filter),
  // '' is the deselect-all sentinel (match nothing), 'a,b' is a subset. Never
  // collapse absent and '' — they mean opposite things.
  const teamGetRowKey = useCallback((ts: Timesheet) => ts.id || '', []);
  const buildTeamQueryParams = useCallback(
    (cursor: string | undefined) => {
      if (cursor) return { cursor, limit: TEAM_FETCH_LIMIT };
      const isAll = teamPeriod === 'all';
      return {
        // The window is one a timesheet's own pay period has to overlap, not
        // an exact match, so a fortnight straddling the edge still counts.
        ...(!isAll && teamDateRange.start_date ? { period_start: teamDateRange.start_date } : {}),
        ...(!isAll && teamDateRange.end_date ? { period_end: teamDateRange.end_date } : {}),
        ...(teamFilters.status !== undefined ? { status: teamFilters.status } : {}),
        ...(teamFilters.payment_status !== undefined ? { payment_status: teamFilters.payment_status } : {}),
        ...(teamFilters.user_id !== undefined ? { user_id: teamFilters.user_id } : {}),
        sort_field: teamSortField,
        sort_direction: teamSortDir,
        limit: TEAM_FETCH_LIMIT,
      };
    },
    [teamFilters, teamPeriod, teamDateRange, teamSortField, teamSortDir]
  );
  const {
    allItems: pendingTimesheets,
    isLoading: reviewLoading,
    hasMore: teamHasMore,
    handleLoadMore: teamLoadMore,
    totalCount: teamTotalCount,
    apiSummary: teamSummary,
    reset: resetTeamList,
  } = usePaginatedQuery<Timesheet, any, any>({
    queryKey: 'team-timesheets',
    buildQueryParams: buildTeamQueryParams,
    queryFn: getTimesheets,
    getRowKey: teamGetRowKey,
  });
  const approvedTimesheets = pendingTimesheets.filter(ts => ts.status === TimesheetStatus.TimesheetStatusApproved);
  const isAdmin = user?.role === 'admin';
  // Sticky rather than read straight off the list: pendingTimesheets empties
  // while a refetch is in flight, so deriving this from it made the whole
  // Team tab — and everything open inside it — unmount and remount whenever
  // a filter changed.
  const [hadReviewRows, setHadReviewRows] = useState(false);
  useEffect(() => {
    if (pendingTimesheets.length > 0) setHadReviewRows(true);
  }, [pendingTimesheets.length]);
  const canReview = isAdmin || timesheetReports.length > 0 || hadReviewRows;

  // Load review timesheet by ID
  const { data: reviewTimesheet, isLoading: reviewTimesheetLoading } = useGetTimesheetsTimesheetId(
    reviewTimesheetId || '',
    { query: { enabled: !!reviewTimesheetId, staleTime: 0 } }
  );

  // Load activities for the review timesheet's period
  const reviewPeriodStart = reviewTimesheet?.period_start || '';
  const { data: reviewActivitiesData } = useGetPayrollActivities(
    { period: reviewPeriodStart },
    { query: { enabled: !!reviewPeriodStart } }
  );
  const reviewActivities = useMemo(() => reviewActivitiesData?.data || [], [reviewActivitiesData]);

  // Load reviewed user's config (for payee info)
  const reviewUserId = reviewTimesheet?.user_id || '';
  const { data: reviewUserConfig } = useGetReviewUserConfig(reviewUserId, {
    query: { enabled: !!reviewUserId },
  });
  const reviewPayee = reviewUserConfig?.payee;

  // Public settings for company name (used in payment memo)
  const { data: publicSettings } = usePublicSettings();

  // Mutations
  const createMutation = usePostTimesheetsUsersUserId();
  const deleteMutation = useDeleteTimesheetsTimesheetId();
  const refreshPaymentMutation = usePostInvoicePaymentsPaymentIdRefresh();
  // Every edit and state change is a PATCH now; see useTimesheetActions for
  // how the old per-verb endpoints map onto it.
  const actions = useTimesheetActions();

  // Initialize local state when timesheet loads, changes, period navigates,
  // or the editor is reopened (showEditor ensures we reset after discard).
  useEffect(() => {
    if (!showEditor) return;
    if (timesheet?.entries) {
      setLocalEntries(
        timesheet.entries.map(e => ({
          activity_id: e.activity_id || '',
          entry_date: e.entry_date || '',
          hours_decimal: e.hours_decimal,
          hours_minutes: e.hours_minutes,
          description: e.description || '',
        }))
      );
    } else {
      setLocalEntries([]);
    }
    setLocalNotes(timesheet?.notes || '');
    setIsDirty(false);
  }, [showEditor, timesheet?.id, timesheet?.entries, timesheet?.notes, periodStart]);


  // Scroll to payment reference section when a payment error appears
  useEffect(() => {
    if (paymentError) {
      paymentRefSection.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [paymentError]);

  // Invalidate data that may have changed on other pages (e.g. config, rates, activities)
  useEffect(() => {
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: getGetPayrollUsersUserIdConfigQueryKey(user.id) });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect invalid tabs or tabs the user can't access
  useEffect(() => {
    // Wait until config has actually loaded (not just "not loading" — disabled queries also report !isLoading)
    if (!configData) return;
    if (!tab || !validTabs.includes(tab)) {
      const defaultTab = timesheetsEnabled ? 'me' : 'team';
      navigate(`/timesheets/${defaultTab}`, { replace: true });
    } else if (tab === 'me' && !timesheetsEnabled) {
      navigate('/timesheets/team', { replace: true });
    }
  }, [tab, navigate, timesheetsEnabled, configData]);

  // Auto-open review modal from ?timesheet=<timesheetId> query param (e.g. from notifications or bookmarks)
  useEffect(() => {
    const timesheetId = searchParams.get('timesheet');
    if (timesheetId && activeTab === 'team' && timesheetId !== reviewTimesheetId) {
      setReviewTimesheetId(timesheetId);
      setReviewRejectReason('');
      setReviewError(null);
    }
  }, [searchParams, activeTab, reviewTimesheetId]);

  // Auto-open editor from ?date=<date> query param (e.g. from bookmarks)
  useEffect(() => {
    const raw = searchParams.get('date');
    if (raw && activeTab === 'me' && !showEditor) {
      // Normalize to YYYY-MM-DD (e.g. "2026-02-8" → "2026-02-08")
      const parts = raw.split('-');
      const parsed = parts.length === 3
        ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
        : new Date(NaN);
      if (!isNaN(parsed.getTime())) {
        const date = parsed.toLocaleDateString('en-CA'); // "YYYY-MM-DD"
        setEditorDate(date);
        setError(null);
        setIsDirty(false);
        setUnsavedAction(null);
        setShowEditor(true);
        if (raw !== date) {
          searchParams.set('date', date);
          setSearchParams(searchParams, { replace: true });
        }
      }
    }
  }, [searchParams, setSearchParams, activeTab, showEditor]);

  // One list endpoint now, so one key prefix to clear. Passing no params
  // matches every /timesheets query — the editor's single-period fetch and
  // the "me" history list alike.
  const invalidateTimesheets = useCallback(() => {
    queryClient.removeQueries({ queryKey: getGetTimesheetsQueryKey() });
    if (timesheet?.id) {
      queryClient.removeQueries({ queryKey: getGetTimesheetsTimesheetIdQueryKey(timesheet.id) });
    }
  }, [queryClient, timesheet?.id]);

  const invalidateReview = useCallback(() => {
    // The team list accumulates pages, so it is reset rather than
    // invalidated: invalidating refetches page one and appends it to the
    // pages already on screen, which is how a list ends up showing the same
    // rows twice after an action.
    resetTeamList();
    queryClient.removeQueries({ queryKey: getGetTimesheetsQueryKey() });
  }, [queryClient, resetTeamList]);

  const handleTabSelect = (newTab: string) => {
    navigate(`/timesheets/${newTab}`, { replace: true });
    if (newTab === 'me') {
      queryClient.invalidateQueries({ queryKey: getGetTimesheetsQueryKey() });
    } else if (newTab === 'team') {
      resetTeamList();
    }
  };

  const openEditor = (date: string) => {
    setEditorDate(date);
    setError(null);
    setIsDirty(false);
    setUnsavedAction(null);
    // Refetch period, timesheet, activities, and user config to get latest data
    queryClient.invalidateQueries({ queryKey: getGetTimesheetsUsersUserIdCurrentPeriodQueryKey('me') });
    queryClient.invalidateQueries({ queryKey: getGetPayrollActivitiesQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetTimesheetsQueryKey({ period_start: periodStart }) });
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: getGetPayrollUsersUserIdConfigQueryKey(user.id) });
    }
    setShowEditor(true);
    searchParams.set('date', date);
    setSearchParams(searchParams, { replace: true });
  };

  const closeEditor = () => {
    if (isDirty && canEdit) {
      setUnsavedAction('close');
      return;
    }
    setShowEditor(false);
    setError(null);
    setShowDeleteConfirm(false);
    setShowSubmitConfirm(false);
    setUnsavedAction(null);
    searchParams.delete('date');
    setSearchParams(searchParams, { replace: true });
    // Refresh history list to pick up any changes
  };

  const handleEditorNavigate = (date: string) => {
    if (isDirty && canEdit) {
      setUnsavedAction(date);
      return;
    }
    setEditorDate(date);
  };

  const confirmUnsavedAction = () => {
    if (!unsavedAction) return;
    setIsDirty(false);
    if (unsavedAction === 'close') {
      setShowEditor(false);
      setError(null);
      setShowDeleteConfirm(false);
      setShowSubmitConfirm(false);
      searchParams.delete('date');
      setSearchParams(searchParams, { replace: true });
      // Refresh history list to pick up any changes
    } else {
      // Navigate to the pending date
      setEditorDate(unsavedAction);
      searchParams.set('date', unsavedAction);
      setSearchParams(searchParams, { replace: true });
    }
    setUnsavedAction(null);
  };

  const resetReviewState = () => {
    setReviewRejectReason('');
    setReviewError(null);
    setPaymentError(null);
    setPaymentReference('');
    setShowPayConfirm(false);
    setShowRejectForm(false);
    setShowMarkAsPaidForm(false);
    setConfirmUnpay(false);
    setConfirmUnapprove(false);
    setPayMemo('');
  };

  const openReviewModal = (timesheetId: string) => {
    queryClient.removeQueries({ queryKey: getGetTimesheetsTimesheetIdQueryKey(timesheetId) });
    setReviewTimesheetId(timesheetId);
    resetReviewState();
    searchParams.set('timesheet', timesheetId);
    setSearchParams(searchParams, { replace: true });
  };

  const closeReviewModal = () => {
    setReviewTimesheetId(null);
    resetReviewState();
    searchParams.delete('timesheet');
    setSearchParams(searchParams, { replace: true });
  };

  const handleCreateTimesheet = async () => {
    setError(null);
    try {
      await createMutation.mutateAsync({ userId: 'me', data: { date: editorDate } });
      invalidateTimesheets();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create timesheet');
    }
  };

  const handleSave = async () => {
    if (!timesheet?.id) return;
    setSaving(true);
    setError(null);
    try {
      await actions.saveEntries(
        timesheet.id,
        localEntries,
        timesheet.entries || [],
        localNotes,
        timesheet.notes || ''
      );
      setIsDirty(false);
      setShowDeleteConfirm(false);
      setShowSubmitConfirm(false);
      invalidateTimesheets();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save entries');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!timesheet?.id) return;
    if (localEntries.length === 0 && !localNotes.trim()) {
      setError('A note is required when submitting a blank timesheet.');
      return;
    }
    if (localEntries.some(e => !e.activity_id)) {
      setError('All entries must have an activity selected before submitting.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await actions.saveEntries(
        timesheet.id,
        localEntries,
        timesheet.entries || [],
        localNotes,
        timesheet.notes || ''
      );
      await actions.setStatus(timesheet.id, 'submitted');
      setIsDirty(false);
      setShowSubmitConfirm(false);
      // Await refetch so the UI switches to read-only before clearing the spinner
      await queryClient.refetchQueries({ queryKey: getGetTimesheetsQueryKey() });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!timesheet?.id) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteMutation.mutateAsync({ timesheetId: timesheet.id });
      setShowDeleteConfirm(false);
      setLocalEntries([]);
      invalidateTimesheets();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete timesheet');
    } finally {
      setDeleting(false);
    }
  };

  // Review handlers
  const handleReviewApprove = async () => {
    if (!reviewTimesheetId) return;
    setReviewProcessingAction('approve');
    setReviewError(null);
    try {
      await actions.setStatus(reviewTimesheetId, 'approved');
      resetReviewState();
      invalidateReview();
      await queryClient.refetchQueries({ queryKey: getGetTimesheetsTimesheetIdQueryKey(reviewTimesheetId) });
    } catch (err: any) {
      setReviewError(err.response?.data?.message || 'Failed to approve timesheet');
    } finally {
      setReviewProcessingAction(null);
    }
  };

  const handleReviewReject = async () => {
    if (!reviewTimesheetId || !reviewRejectReason.trim()) return;
    setReviewProcessingAction('reject');
    setReviewError(null);
    try {
      await actions.setStatus(reviewTimesheetId, 'rejected', rejectionOps(reviewRejectReason));
      resetReviewState();
      invalidateReview();
      await queryClient.refetchQueries({ queryKey: getGetTimesheetsTimesheetIdQueryKey(reviewTimesheetId) });
    } catch (err: any) {
      setReviewError(err.response?.data?.message || 'Failed to reject timesheet');
    } finally {
      setReviewProcessingAction(null);
    }
  };

  const handleReviewPay = async () => {
    if (!reviewTimesheetId) return;
    if (!paymentReference.trim()) {
      setPaymentError('Payment reference is required');
      return;
    }
    setPaymentError(null);
    setReviewProcessingAction('markAsPaid');
    setReviewError(null);
    try {
      await actions.setStatus(reviewTimesheetId, 'paid', paymentReferenceOps(paymentReference || undefined));
      resetReviewState();
      invalidateReview();
      await queryClient.refetchQueries({ queryKey: getGetTimesheetsTimesheetIdQueryKey(reviewTimesheetId) });
    } catch (err: any) {
      setReviewError(err.response?.data?.message || 'Failed to mark timesheet as paid');
    } finally {
      setReviewProcessingAction(null);
    }
  };

  const handleOpenPayConfirm = () => {
    if (!reviewTimesheet) return;
    const companyName = publicSettings?.companyName || '';
    const start = reviewTimesheet.period_start || '';
    const end = reviewTimesheet.period_end || '';
    const defaultMemo = `${companyName} Pay for Timesheet ${start} - ${end}`.trim();
    setPayMemo(defaultMemo);
    setShowPayConfirm(true);
    setShowMarkAsPaidForm(false);
  };

  const handleSendPayment = async () => {
    if (!reviewTimesheetId) return;
    setReviewProcessingAction('sendPayment');
    setReviewError(null);
    try {
      await actions.setStatus(reviewTimesheetId, 'processing', sendPaymentOps(payMemo || undefined));
      resetReviewState();
      invalidateReview();
      await queryClient.refetchQueries({ queryKey: getGetTimesheetsTimesheetIdQueryKey(reviewTimesheetId) });
    } catch (err: any) {
      setReviewError(err.response?.data?.message || 'Failed to send payment');
    } finally {
      setReviewProcessingAction(null);
    }
  };

  const handleReviewUnpay = async () => {
    if (!reviewTimesheetId) return;
    setReviewProcessingAction('unpay');
    setReviewError(null);
    try {
      await actions.setStatus(reviewTimesheetId, 'approved');
      resetReviewState();
      invalidateReview();
      await queryClient.refetchQueries({ queryKey: getGetTimesheetsTimesheetIdQueryKey(reviewTimesheetId) });
    } catch (err: any) {
      setReviewError(err.response?.data?.message || 'Failed to undo payment');
    } finally {
      setReviewProcessingAction(null);
    }
  };

  const handleReviewUnapprove = async () => {
    if (!reviewTimesheetId) return;
    setReviewProcessingAction('unapprove');
    setReviewError(null);
    try {
      await actions.setStatus(reviewTimesheetId, 'submitted');
      resetReviewState();
      invalidateReview();
      await queryClient.refetchQueries({ queryKey: getGetTimesheetsTimesheetIdQueryKey(reviewTimesheetId) });
    } catch (err: any) {
      setReviewError(err.response?.data?.message || 'Failed to unapprove timesheet');
    } finally {
      setReviewProcessingAction(null);
    }
  };

  const handlePrintTimesheet = (employeeName: string, pStart?: string, pEnd?: string) => {
    // Find the currently-open modal's content
    const modal = document.querySelector('.modal.show');
    if (!modal) return;

    const period = formatPeriod(pStart, pEnd);
    const title = `Timesheet - ${employeeName}${period ? ` - ${period}` : ''}`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Copy all stylesheets from the parent document
    const styleSheets = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map(el => el.outerHTML)
      .join('\n');

    // Clone the modal body content (skip footer buttons, close button, etc.)
    const modalHeader = modal.querySelector('.modal-header');
    const modalBody = modal.querySelector('.modal-body');
    const headerHtml = modalHeader ? modalHeader.innerHTML : '';

    const bodyHtml = modalBody?.innerHTML || '';

    printWindow.document.write(`<!DOCTYPE html>
<html><head><title>${title}</title>
${styleSheets}
<style>
  /* Print-view overrides for the new window */
  body {
    background: #fff !important;
    padding: 20px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  /* Hide interactive elements */
  .btn, .btn-close, .form-control, .form-select, .print-hide { display: none !important; }
  /* In print, hide editable grid and show review grid */
  .editable-grid { display: none !important; }
  .print-review-grid { display: block !important; }
  /* Remove scroll constraints */
  [style*="max-height"], [style*="maxHeight"] { max-height: none !important; overflow: visible !important; }
  /* Hide scroll shadow overlays */
  [style*="linear-gradient"] { display: none !important; }
  /* Unstick table headers */
  thead[style*="sticky"], thead { position: static !important; }
  /* Clean up modal header styling */
  .modal-header { border: none; padding: 0 0 12px 0; }
  .modal-title { font-size: 18px; }
  /* Clean up modal body */
  .modal-body { padding: 0; overflow: visible !important; }
  @media print {
    body { padding: 0; }
    @page { margin: 0.5in; }
  }
</style>
</head><body>
<div class="modal-header">${headerHtml}</div>
<div class="modal-body">${bodyHtml}</div>
</body></html>`);

    printWindow.document.close();
    try { printWindow.history.replaceState(null, '', window.location.href); } catch (_) { /* ignore */ }
    printWindow.addEventListener('load', () => {
      printWindow.focus();
      printWindow.print();
    });
  };

  const handleBatchPay = async () => {
    if (selectedTimesheetIds.size === 0) return;
    if (!paymentReference.trim()) {
      setBatchPayError('Payment reference is required');
      return;
    }
    setBatchPaying(true);
    setBatchPayError(null);
    try {
      await actions.payMany(Array.from(selectedTimesheetIds), paymentReference || undefined);
      invalidateReview();
      setSelectedTimesheetIds(new Set());
      setPaymentReference('');
    } catch (err: any) {
      setBatchPayError(err.response?.data?.message || 'Failed to batch mark timesheets as paid');
    } finally {
      setBatchPaying(false);
    }
  };

  const toggleTimesheetSelection = (id: string) => {
    setSelectedTimesheetIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAllApproved = () => {
    if (selectedTimesheetIds.size === approvedTimesheets.length) {
      setSelectedTimesheetIds(new Set());
    } else {
      setSelectedTimesheetIds(new Set(approvedTimesheets.map(ts => ts.id!)));
    }
  };

  // Permission helpers
  const isOwner = timesheet?.user_id === user?.id;
  const status = timesheet?.status;
  const today = new Date().toLocaleDateString('en-CA');
  const isFuturePeriod = periodStart > today;
  const canEdit = !isFuturePeriod && (!timesheet || status === TimesheetStatus.TimesheetStatusDraft || status === TimesheetStatus.TimesheetStatusRejected);
  const canSubmit = !isFuturePeriod && isOwner && (status === TimesheetStatus.TimesheetStatusDraft || status === TimesheetStatus.TimesheetStatusRejected);
  const canDelete = isOwner && (status === TimesheetStatus.TimesheetStatusDraft || status === TimesheetStatus.TimesheetStatusRejected);

  // entriesPending is part of loading, not a detail: the grid seeds its cells
  // once, when it mounts, so mounting it against a row whose entries have not
  // arrived leaves it permanently empty.
  const editorLoading = periodLoading || timesheetsLoading || entriesPending;

  // Build summary entries from local edits + rate schedules.
  // For editable timesheets (draft/rejected): compute per-entry-date rates from
  //   the activity rate schedule returned by the activities API.
  // For read-only timesheets (submitted/approved/paid): use locked-in entry rates.
  const summaryEntries: TimesheetEntry[] = localEntries.map(e => {
    const activity = e.activity_id ? activities.find(a => a.id === e.activity_id) : undefined;
    const hours = e.hours_minutes ? parseHoursInput(e.hours_minutes) : (e.hours_decimal ?? 0);
    let rate: number;
    if (canEdit) {
      // Draft/rejected: compute effective rate per entry date from rate schedule
      rate = findEffectiveRate(userDefaultRate, activity, e.entry_date);
    } else {
      // Submitted/approved/paid: use locked-in rate from server
      const serverEntry = timesheet?.entries?.find(
        se => se.activity_id === e.activity_id && se.entry_date === e.entry_date
      );
      rate = serverEntry?.hourly_rate ?? findEffectiveRate(userDefaultRate, activity, e.entry_date);
    }
    return {
      activity_id: e.activity_id,
      activity_name: activity?.name,
      activity_category: activity?.category,
      hours,
      hourly_rate: rate,
      amount: roundCents(hours * rate),
    };
  });

  // Always use summaryEntries derived from localEntries (the grid's source of truth)
  const effectiveSummaryEntries = summaryEntries;

  // Tab visibility
  const showMyTimesheets = timesheetsEnabled;
  const showReview = canReview;
  const showTabs = showMyTimesheets && showReview;

  // Deliberately not gated on the team list's loading state. It was, and that
  // made every filter change swap the whole page for this spinner and back —
  // unmounting the table, the filter bar and whatever dropdown was open mid
  // click. The table takes isLoading itself and shows progress in place.
  //
  // configData rather than configLoading: a disabled react-query reports
  // isLoading false while data is still undefined, so gating on the flag alone
  // let the page render with timesheetsEnabled=false, then flip once the
  // config landed — which swapped the standalone branch for the tabbed one and
  // remounted everything a second time. isError keeps that from hanging
  // forever if the config never arrives.
  if (!user || configLoading || (!configData && !configError)) {
    return (
      <DashboardLayout title="Timesheets">
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading...
        </div>
      </DashboardLayout>
    );
  }

  if (!showMyTimesheets && !showReview) {
    return (
      <DashboardLayout title="Timesheets">
        <Alert variant="info" className="mt-3">
          <i className="bi bi-info-circle me-2"></i>
          Timesheets are not enabled for your account. Contact your administrator to get started.
        </Alert>
      </DashboardLayout>
    );
  }

  // Review modal: employee name
  const reviewOwnerName = reviewTimesheet?.user
    ? `${reviewTimesheet.user.first_name || ''} ${reviewTimesheet.user.last_name || ''}`.trim() || reviewTimesheet.user.email
    : '';

  return (
    <DashboardLayout title={activeTab === 'team' && !showMyTimesheets ? "My Team's Timesheets" : "Timesheets"} scrollable>
      <div className="mt-3 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
        {showTabs ? (
          <Tabs activeKey={activeTab} onSelect={(k) => k && handleTabSelect(k)} mountOnEnter unmountOnExit>
            {showMyTimesheets && (
              <Tab eventKey="me" title="My Timesheets">
                <MyTimesheetsContent
                  historyLoading={historyLoading}
                  allTimesheets={allTimesheets}
                  openEditor={openEditor}
                  userId={user?.id}
                  userPreferences={userData?.preferences}
                />
              </Tab>
            )}
            {showReview && (
              <Tab eventKey="team" title={<>Team{pendingTimesheets.length > 0 && <Badge bg="danger" pill className="ms-2">{pendingTimesheets.length}</Badge>}</>}>
                <TeamTimesheetsContent
                  reviewLoading={reviewLoading}
                  pendingTimesheets={pendingTimesheets}
                  approvedTimesheets={approvedTimesheets}
                  isAdmin={isAdmin}
                  selectedTimesheetIds={selectedTimesheetIds}
                  toggleAllApproved={toggleAllApproved}
                  toggleTimesheetSelection={toggleTimesheetSelection}
                  openReviewModal={openReviewModal}
                  filters={teamFilters}
                  onFilterChange={handleTeamFilterChange}
                  onClearFilters={handleTeamClearFilters}
                  period={teamPeriod}
                  dateRange={teamDateRange}
                  onPeriodChange={handleTeamPeriodChange}
                  summary={teamSummary}
                  sortField={teamSortField}
                  sortDir={teamSortDir}
                  handleSortChange={handleTeamSortChange}
                  hasMore={teamHasMore}
                  onLoadMore={teamLoadMore}
                  totalCount={teamTotalCount}
                  setSelectedTimesheetIds={setSelectedTimesheetIds}
                  paymentReference={paymentReference}
                  setPaymentReference={setPaymentReference}
                  handleBatchPay={handleBatchPay}
                  batchPaying={batchPaying}
                  batchPayError={batchPayError}
                  setBatchPayError={setBatchPayError}
                  userId={user?.id}
                  userPreferences={userData?.preferences}
                />
              </Tab>
            )}
          </Tabs>
        ) : showMyTimesheets ? (
          <MyTimesheetsContent
            historyLoading={historyLoading}
            allTimesheets={allTimesheets}
            openEditor={openEditor}
          />
        ) : showReview ? (
          <TeamTimesheetsContent
            reviewLoading={reviewLoading}
            pendingTimesheets={pendingTimesheets}
            approvedTimesheets={approvedTimesheets}
            isAdmin={isAdmin}
            selectedTimesheetIds={selectedTimesheetIds}
            toggleAllApproved={toggleAllApproved}
            toggleTimesheetSelection={toggleTimesheetSelection}
            openReviewModal={openReviewModal}
            filters={teamFilters}
            onFilterChange={handleTeamFilterChange}
            onClearFilters={handleTeamClearFilters}
            period={teamPeriod}
            dateRange={teamDateRange}
            onPeriodChange={handleTeamPeriodChange}
            summary={teamSummary}
            sortField={teamSortField}
            sortDir={teamSortDir}
            handleSortChange={handleTeamSortChange}
            hasMore={teamHasMore}
            onLoadMore={teamLoadMore}
            totalCount={teamTotalCount}
            setSelectedTimesheetIds={setSelectedTimesheetIds}
            paymentReference={paymentReference}
            setPaymentReference={setPaymentReference}
            handleBatchPay={handleBatchPay}
            batchPaying={batchPaying}
            batchPayError={batchPayError}
            setBatchPayError={setBatchPayError}
            userId={user?.id}
            userPreferences={userData?.preferences}
          />
        ) : null}

        {/* Editor Modal */}
        <Modal show={showEditor} onHide={closeEditor} size="xl" scrollable>
          <Modal.Header closeButton>
            <Modal.Title>
              {timesheet ? 'Edit Timesheet' : 'New Timesheet'}
              {timesheet && <span className="ms-2">{statusBadge(timesheet.status || 'draft')}</span>}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Period navigation + Summary */}
            {!editorLoading && periodStart && periodEnd && (
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                <div className="flex-grow-1">
                  <TimesheetPeriodNavigation
                    periodStart={periodStart}
                    periodEnd={periodEnd}
                    payPeriodType={payPeriodType}
                    onNavigate={handleEditorNavigate}
                  />
                </div>
                <div className="flex-grow-1 d-flex justify-content-end">
                  <TimesheetSummary entries={effectiveSummaryEntries} />
                </div>
              </div>
            )}

            {editorLoading && (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading timesheet...
              </div>
            )}

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
                {error}
              </Alert>
            )}

            {/* Rejection reason */}
            {timesheet?.rejection_reason && status === TimesheetStatus.TimesheetStatusRejected && (
              <Alert variant="warning" className="mb-3">
                <strong>Rejected:</strong> {timesheet.rejection_reason}
              </Alert>
            )}

            {/* No timesheet for this period */}
            {!editorLoading && !timesheet && periodStart && (
              <div className="text-center py-4">
                {isFuturePeriod ? (
                  <p className="text-muted mb-0">This is a future period. You can create a timesheet once the period starts.</p>
                ) : (
                  <>
                    <p className="text-muted mb-3">No timesheet exists for this period.</p>
                    <Button variant="primary" onClick={handleCreateTimesheet}>
                      <i className="bi bi-plus-lg me-1"></i>Create Timesheet
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* View toggle + Grid */}
            {!editorLoading && timesheet && periodStart && periodEnd && (
              <>
              {canEdit && (
              <ButtonGroup size="sm" className="mb-2">
                <Button
                  variant={viewMode === 'day' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('day')}
                >
                  <i className="bi bi-calendar-day me-1"></i>Day
                </Button>
                <Button
                  variant={viewMode === 'month' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('month')}
                >
                  <i className="bi bi-calendar3 me-1"></i>Full Period
                </Button>
              </ButtonGroup>
              )}
              <div className={canEdit ? "editable-grid" : ""}>
                <TimesheetWeeklyGrid
                  entries={timesheet.entries || []}
                  activities={activities}
                  periodStart={periodStart}
                  periodEnd={periodEnd}
                  reviewMode={!canEdit}
                  onChange={entries => { setIsDirty(true); setLocalEntries(entries); }}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  defaultHourlyRate={userDefaultRate}
                />
              </div>
              {/* Hidden review-mode grid for printing */}
              {canEdit && (
                <div className="print-review-grid" style={{ display: 'none' }}>
                  <TimesheetWeeklyGrid
                    entries={timesheet.entries || []}
                    activities={activities}
                    periodStart={periodStart}
                    periodEnd={periodEnd}
                    reviewMode={true}
                    onChange={() => {}}
                    viewMode="month"
                    defaultHourlyRate={userDefaultRate}
                  />
                </div>
              )}
              <div className="mt-3">
                <Form.Label className="fw-semibold">Notes</Form.Label>
                {localEntries.length === 0 && (
                  <Form.Text className="text-muted d-block mb-1">Required to submit an empty timesheet</Form.Text>
                )}
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={localNotes}
                  onChange={e => { setIsDirty(true); setLocalNotes(e.target.value); }}
                  disabled={!canEdit}
                  placeholder="Add notes to this timesheet..."
                  style={{ resize: 'vertical' }}
                />
              </div>
              </>
            )}

          </Modal.Body>
          <Modal.Footer>
            {showDeleteConfirm && (
              <div className="me-auto d-flex align-items-center gap-2">
                <span className="text-danger fw-semibold">Delete this draft?</span>
                <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting}>
                  {deleting ? <><Spinner animation="border" size="sm" className="me-1" />Deleting...</> : 'Yes, Delete'}
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              </div>
            )}
            {showSubmitConfirm && (
              <div className="me-auto d-flex align-items-center gap-2">
                <span className="text-success fw-semibold">Submit this timesheet?</span>
                <Button variant="success" size="sm" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? <><Spinner animation="border" size="sm" className="me-1" />Submitting...</> : 'Yes, Submit'}
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => setShowSubmitConfirm(false)}>Cancel</Button>
              </div>
            )}
            {unsavedAction && (
              <div className="d-flex align-items-center gap-2">
                <span className="text-warning fw-semibold">You have unsaved changes.</span>
                <Button variant="warning" size="sm" onClick={confirmUnsavedAction}>Discard Changes</Button>
                <Button variant="outline-secondary" size="sm" onClick={() => setUnsavedAction(null)}>Cancel</Button>
              </div>
            )}
            {!showDeleteConfirm && !showSubmitConfirm && !unsavedAction && (
              <>
                {canDelete && (
                  <Button variant="outline-danger" className="me-auto" onClick={() => { setShowDeleteConfirm(true); setShowSubmitConfirm(false); setUnsavedAction(null); }}>
                    <i className="bi bi-trash me-1"></i>Delete
                  </Button>
                )}
                {canEdit && timesheet && (
                  <Button variant="primary" onClick={handleSave} disabled={saving || !isDirty}>
                    {saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving...</> : <><i className="bi bi-save me-1"></i>Save</>}
                  </Button>
                )}
                {canSubmit && (
                  <Button variant="success" onClick={() => { setShowSubmitConfirm(true); setShowDeleteConfirm(false); setUnsavedAction(null); }} disabled={localEntries.length === 0 && !localNotes.trim()}>
                    <i className="bi bi-send me-1"></i>Submit for Approval
                  </Button>
                )}
                <Button variant="outline-secondary" onClick={() => handlePrintTimesheet(`${user?.first_name || ''} ${user?.last_name || ''}`.trim(), periodStart, periodEnd)} title={isDirty ? "Save changes before printing" : "Print timesheet"} disabled={isDirty}>
                  <i className="bi bi-printer me-1"></i>Print
                </Button>
                <Button variant="secondary" onClick={closeEditor}>Close</Button>
              </>
            )}
          </Modal.Footer>
        </Modal>

        {/* Review Modal */}
        <Modal show={!!reviewTimesheetId} onHide={closeReviewModal} size="xl" scrollable>
          <Modal.Header closeButton>
            <Modal.Title>
              Review Timesheet
              {reviewOwnerName && <span className="ms-2 fs-6 text-muted fw-normal">- {reviewOwnerName}</span>}
              {reviewTimesheet && <span className="ms-2">{statusBadge(reviewTimesheet.status || 'submitted')}</span>}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {reviewTimesheetLoading && (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading timesheet...
              </div>
            )}

            {reviewError && (
              <Alert variant="danger" dismissible onClose={() => setReviewError(null)} className="mb-3">
                {reviewError}
              </Alert>
            )}

            {reviewTimesheet && !reviewTimesheetLoading && (
              <>
                {/* Period + Summary */}
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                  <div>
                    <div className="fw-semibold">
                      {formatPeriod(reviewTimesheet.period_start, reviewTimesheet.period_end)}
                    </div>
                    {reviewTimesheet.submitted_at && (
                      <small className="text-muted">Submitted {formatDate(reviewTimesheet.submitted_at)}</small>
                    )}
                  </div>
                  {(reviewTimesheet.entries?.length ?? 0) > 0 && (
                    <div className="d-flex justify-content-end">
                      <TimesheetSummary entries={reviewTimesheet.entries || []} />
                    </div>
                  )}
                </div>

                {/* Read-only Grid */}
                {reviewTimesheet.period_start && reviewTimesheet.period_end && (
                  <TimesheetWeeklyGrid
                    entries={reviewTimesheet.entries || []}
                    activities={reviewActivities}
                    periodStart={reviewTimesheet.period_start}
                    periodEnd={reviewTimesheet.period_end}
                    reviewMode={true}
                    onChange={() => {}}
                  />
                )}

                {/* Employee notes (read-only) */}
                {reviewTimesheet.notes && (
                  <div className="mt-3">
                    <strong>Notes from employee:</strong>
                    <p className="mt-1 mb-0 text-muted" style={{ whiteSpace: 'pre-wrap' }}>{reviewTimesheet.notes}</p>
                  </div>
                )}

                {/* Rejection notes - shown when reject action is initiated */}
                {showRejectForm && (reviewTimesheet.status === TimesheetStatus.TimesheetStatusSubmitted || reviewTimesheet.status === TimesheetStatus.TimesheetStatusApproved) && (
                  <Form.Group className="mt-3 print-hide">
                    <Form.Label className="fw-semibold">Rejection notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={reviewRejectReason}
                      onChange={e => setReviewRejectReason(e.target.value)}
                      placeholder="Required when rejecting - explain what needs to be corrected..."
                      style={{ resize: 'vertical' }}
                      autoFocus
                    />
                    <Form.Text className="text-muted">
                      The employee will see this note so they can address the issue.
                    </Form.Text>
                  </Form.Group>
                )}

                {/* Pay confirmation - for approved timesheets (admin only) */}
                {reviewTimesheet.status === TimesheetStatus.TimesheetStatusApproved && isAdmin && showPayConfirm && !showMarkAsPaidForm && reviewPayee && (
                  <div className="mt-3 p-3 border rounded bg-body-tertiary print-hide">
                    <h6 className="fw-semibold mb-2"><i className="bi bi-bank me-1"></i>Send Payment</h6>
                    <div className="mb-2">
                      <div><strong>Payee:</strong> {reviewPayee.name}{reviewPayee.nickname ? ` (${reviewPayee.nickname})` : ''}</div>
                      {reviewPayee.emails && reviewPayee.emails.length > 0 && (
                        <div><strong>Email:</strong> {reviewPayee.emails.join(', ')}</div>
                      )}
                      <div><strong>Amount:</strong> ${reviewTimesheet.total_amount?.toFixed(2)}</div>
                      <div><strong>Method:</strong> {reviewPayee.payment_method || 'ACH'}</div>
                    </div>
                    <Form.Group className="mb-2">
                      <Form.Label className="fw-semibold">Memo (visible to recipient)</Form.Label>
                      <Form.Control
                        type="text"
                        value={payMemo}
                        onChange={e => setPayMemo(e.target.value)}
                        placeholder="Payment memo..."
                      />
                    </Form.Group>
                    <div className="d-flex gap-2">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleSendPayment}
                        disabled={!!reviewProcessingAction}
                      >
                        {reviewProcessingAction === 'sendPayment' ? (
                          <><Spinner animation="border" size="sm" className="me-1" />Sending...</>
                        ) : (
                          'Confirm & Send'
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setShowPayConfirm(false)}
                        disabled={!!reviewProcessingAction}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Payment reference input - shown when mark as paid action is initiated */}
                {showMarkAsPaidForm && reviewTimesheet.status === TimesheetStatus.TimesheetStatusApproved && isAdmin && (
                  <div ref={paymentRefSection} className="mt-3 print-hide">
                    {paymentError && (
                      <Alert variant="danger" dismissible onClose={() => setPaymentError(null)} className="mb-2">
                        {paymentError}
                      </Alert>
                    )}
                    <Form.Group>
                      <Form.Label className="fw-semibold">Payment reference</Form.Label>
                      <Form.Control
                        type="text"
                        value={paymentReference}
                        onChange={e => { setPaymentReference(e.target.value); setPaymentError(null); }}
                        placeholder="Payment reference (e.g. check number, transaction ID)..."
                        isInvalid={!!paymentError}
                        required
                        autoFocus
                      />
                    </Form.Group>
                  </div>
                )}

                {/* Processing info - for processing timesheets */}
                {reviewTimesheet.status === TimesheetStatus.TimesheetStatusProcessing && (
                  <div className="mt-3 p-3 border rounded bg-body-tertiary">
                    <h6 className="fw-semibold mb-2"><i className="bi bi-hourglass-split me-1"></i>Payment Processing</h6>
                    <p className="mb-2 text-muted">Payment has been initiated and is being processed. This may take several business days.</p>
                    {reviewTimesheet.payment && (
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <span><strong>Status:</strong></span>
                          <Badge bg={
                            reviewTimesheet.payment.status === 'sent' ? 'success' :
                            reviewTimesheet.payment.status === 'failed' ? 'danger' :
                            reviewTimesheet.payment.status === 'rejected' ? 'danger' :
                            reviewTimesheet.payment.status === 'cancelled' ? 'secondary' :
                            'warning'
                          }>
                            {reviewTimesheet.payment.status_label || reviewTimesheet.payment.status}
                          </Badge>
                          {isAdmin && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={refreshPaymentMutation.isPending}
                              onClick={async () => {
                                try {
                                  await refreshPaymentMutation.mutateAsync({ paymentId: reviewTimesheet.payment!.id! });
                                  await queryClient.refetchQueries({ queryKey: getGetTimesheetsTimesheetIdQueryKey(reviewTimesheetId!) });
                                  invalidateReview();
                                } catch (err: any) {
                                  setReviewError(err.response?.data?.message || 'Failed to refresh payment status');
                                }
                              }}
                              title="Refresh payment status from provider"
                            >
                              {refreshPaymentMutation.isPending ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <i className="bi bi-arrow-clockwise"></i>
                              )}
                            </Button>
                          )}
                        </div>
                        <div><strong>Amount:</strong> ${reviewTimesheet.payment.amount?.toFixed(2)}</div>
                        <div><strong>Method:</strong> {reviewTimesheet.payment.payment_method?.toUpperCase()}</div>
                        {reviewTimesheet.payment.error_message && (
                          <div className="text-danger mt-1"><strong>Error:</strong> {reviewTimesheet.payment.error_message}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Payment info - for paid timesheets */}
                {reviewTimesheet.status === TimesheetStatus.TimesheetStatusPaid && (
                  <div className="mt-3">
                    <strong>Payment Information</strong>
                    <div className="mt-1 text-muted">
                      <div>Paid: {formatDate(reviewTimesheet.paid_at)}</div>
                      {reviewTimesheet.payment ? (
                        <div className="mt-1">
                          <div className="d-flex align-items-center gap-2">
                            <span>Status:</span>
                            <Badge bg={
                              reviewTimesheet.payment.status === 'sent' ? 'success' :
                              reviewTimesheet.payment.status === 'processing' ? 'info' :
                              reviewTimesheet.payment.status === 'approved' ? 'success' :
                              reviewTimesheet.payment.status === 'rejected' ? 'danger' :
                              reviewTimesheet.payment.status === 'failed' ? 'danger' :
                              reviewTimesheet.payment.status === 'cancelled' ? 'secondary' :
                              'warning'
                            }>
                              {reviewTimesheet.payment.status_label || reviewTimesheet.payment.status}
                            </Badge>
                            {isAdmin && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={refreshPaymentMutation.isPending}
                                onClick={async () => {
                                  try {
                                    await refreshPaymentMutation.mutateAsync({ paymentId: reviewTimesheet.payment!.id! });
                                    await queryClient.refetchQueries({ queryKey: getGetTimesheetsTimesheetIdQueryKey(reviewTimesheetId!) });
                                    invalidateReview();
                                  } catch (err: any) {
                                    setReviewError(err.response?.data?.message || 'Failed to refresh payment status');
                                  }
                                }}
                                title="Refresh payment status from provider"
                              >
                                {refreshPaymentMutation.isPending ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <i className="bi bi-arrow-clockwise"></i>
                                )}
                              </Button>
                            )}
                          </div>
                          <div>Amount: ${reviewTimesheet.payment.amount?.toFixed(2)}</div>
                          <div>Method: {reviewTimesheet.payment.payment_method?.toUpperCase()}</div>
                          {reviewTimesheet.payment.error_message && (
                            <div className="text-danger">Error: {reviewTimesheet.payment.error_message}</div>
                          )}
                        </div>
                      ) : reviewTimesheet.payment_reference ? (
                        <div>Reference: {reviewTimesheet.payment_reference}</div>
                      ) : null}
                    </div>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {/* Submitted: approve/reject */}
            {reviewTimesheet?.status === TimesheetStatus.TimesheetStatusSubmitted && (
              <>
                <Button
                  variant="success"
                  onClick={handleReviewApprove}
                  disabled={!!reviewProcessingAction || reviewTimesheetLoading}
                >
                  {reviewProcessingAction === 'approve' ? (
                    <><Spinner animation="border" size="sm" className="me-1" />Approving...</>
                  ) : (
                    <><i className="bi bi-check-lg me-1"></i>Approve</>
                  )}
                </Button>
                <Button
                  variant="danger"
                  onClick={showRejectForm ? handleReviewReject : () => setShowRejectForm(true)}
                  disabled={(showRejectForm && !reviewRejectReason.trim()) || !!reviewProcessingAction || reviewTimesheetLoading}
                >
                  <i className="bi bi-x-lg me-1"></i>Reject
                </Button>
              </>
            )}
            {/* Approved: pay, mark as paid, reject, or unapprove */}
            {reviewTimesheet?.status === TimesheetStatus.TimesheetStatusApproved && (
              <>
                {isAdmin && (
                  <Button
                    variant="success"
                    onClick={handleOpenPayConfirm}
                    disabled={!reviewPayee || !!reviewProcessingAction || reviewTimesheetLoading}
                    title={!reviewPayee ? 'User is not linked to a payee' : 'Send payment to payee'}
                  >
                    <i className="bi bi-bank me-1"></i>Pay
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    variant="info"
                    onClick={showMarkAsPaidForm ? handleReviewPay : () => { setShowMarkAsPaidForm(true); setShowPayConfirm(false); }}
                    disabled={!!reviewProcessingAction || reviewTimesheetLoading}
                  >
                    {reviewProcessingAction === 'markAsPaid' ? (
                      <><Spinner animation="border" size="sm" className="me-1" />Processing...</>
                    ) : (
                      <><i className="bi bi-cash me-1"></i>Mark as Paid</>
                    )}
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    variant="danger"
                    onClick={showRejectForm ? handleReviewReject : () => setShowRejectForm(true)}
                    disabled={(showRejectForm && !reviewRejectReason.trim()) || !!reviewProcessingAction || reviewTimesheetLoading}
                  >
                    <i className="bi bi-x-lg me-1"></i>Reject
                  </Button>
                )}
                {!confirmUnapprove ? (
                  <Button
                    variant="outline-warning"
                    onClick={() => setConfirmUnapprove(true)}
                    disabled={!!reviewProcessingAction || reviewTimesheetLoading}
                  >
                    <i className="bi bi-arrow-counterclockwise me-1"></i>Unapprove
                  </Button>
                ) : (
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-warning fw-semibold">Revert to submitted?</span>
                    <Button variant="warning" size="sm" onClick={handleReviewUnapprove} disabled={!!reviewProcessingAction}>
                      {reviewProcessingAction ? <><Spinner animation="border" size="sm" className="me-1" />Processing...</> : 'Yes, Unapprove'}
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => setConfirmUnapprove(false)}>Cancel</Button>
                  </div>
                )}
              </>
            )}
            {/* Processing with failed payment: reissue (admin only) */}
            {reviewTimesheet?.status === TimesheetStatus.TimesheetStatusProcessing && isAdmin &&
              reviewTimesheet?.payment &&
              ['failed', 'rejected', 'cancelled'].includes(reviewTimesheet.payment.status || '') && (
              <>
                {!showPayConfirm ? (
                  <Button
                    variant="warning"
                    onClick={handleOpenPayConfirm}
                    disabled={!!reviewProcessingAction || reviewTimesheetLoading}
                  >
                    <i className="bi bi-arrow-repeat me-1"></i>Reissue Payment Request
                  </Button>
                ) : (
                  <div className="mt-3 p-3 border rounded bg-body-tertiary w-100 mb-2">
                    <h6 className="fw-semibold mb-2"><i className="bi bi-arrow-repeat me-1"></i>Reissue Payment</h6>
                    <div className="mb-2">
                      <div><strong>Payee:</strong> {reviewPayee?.name}{reviewPayee?.nickname ? ` (${reviewPayee.nickname})` : ''}</div>
                      <div><strong>Amount:</strong> ${reviewTimesheet.total_amount?.toFixed(2)}</div>
                      <div><strong>Method:</strong> {reviewPayee?.payment_method || 'ACH'}</div>
                    </div>
                    <Form.Group className="mb-2">
                      <Form.Label className="fw-semibold">Memo (visible to recipient)</Form.Label>
                      <Form.Control
                        type="text"
                        value={payMemo}
                        onChange={e => setPayMemo(e.target.value)}
                        placeholder="Payment memo..."
                      />
                    </Form.Group>
                    <div className="d-flex gap-2">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleSendPayment}
                        disabled={!!reviewProcessingAction}
                      >
                        {reviewProcessingAction === 'sendPayment' ? (
                          <><Spinner animation="border" size="sm" className="me-1" />Sending...</>
                        ) : (
                          'Confirm & Send'
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setShowPayConfirm(false)}
                        disabled={!!reviewProcessingAction}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
            {/* Paid/Processing: undo payment (admin only) */}
            {(reviewTimesheet?.status === TimesheetStatus.TimesheetStatusPaid || reviewTimesheet?.status === TimesheetStatus.TimesheetStatusProcessing) && isAdmin && (
              <>
                {!confirmUnpay ? (
                  <Button
                    variant="outline-warning"
                    onClick={() => setConfirmUnpay(true)}
                    disabled={!!reviewProcessingAction || reviewTimesheetLoading}
                  >
                    <i className="bi bi-arrow-counterclockwise me-1"></i>Undo Payment
                  </Button>
                ) : (
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-warning fw-semibold">Revert to approved?</span>
                    <Button variant="warning" size="sm" onClick={handleReviewUnpay} disabled={!!reviewProcessingAction}>
                      {reviewProcessingAction ? <><Spinner animation="border" size="sm" className="me-1" />Processing...</> : 'Yes, Undo Payment'}
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => setConfirmUnpay(false)}>Cancel</Button>
                  </div>
                )}
              </>
            )}
            {reviewTimesheet && !reviewTimesheetLoading && (
              <Button variant="outline-secondary" onClick={() => handlePrintTimesheet(reviewOwnerName || '', reviewTimesheet.period_start, reviewTimesheet.period_end)} title="Print timesheet">
                <i className="bi bi-printer me-1"></i>Print
              </Button>
            )}
            <Button variant="secondary" onClick={closeReviewModal}>Close</Button>
          </Modal.Footer>
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default TimesheetsPage;
